import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "./styles/index.scss";
import "lazysizes";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import Context from "./Context";
import { useService } from "./base/hooks/useService";
import { StorageService } from "@services/StorageService";
import { localstorageKeys } from "@const/store";
import { startSocket, stopSocket } from "@redux/socket/actions";
import useClearOldStorageKeys from "./hooks/useClearOldStorageKeys";
import keyVersions from "./const/keyVersions";
import useHandleConsentFormLink from "./hooks/useHandleConsentFormLink";
import { TrackingLinksService } from "@services/TrackingLinksService";
import {
  initGoogleAnalytic,
  sendGoogleAnalyticEvent,
} from "@helpers/googleAnalytic";
import { toggleSignInModal } from "@redux/signIn/actions";
import routes from "@const/routes";

const blackListPagesToSocketConnect = [routes.COMPLETE_PAYMENT_LIVESTREAM()];
import useOAuth from "./hooks/useOAuth";
import i18n, { LANGUAGE_OPTIONS } from "@/i18n";
import changeLanguage from "@helpers/changeLanguage";

const App = () => {
  useOAuth();

  const isPageFromBlackList = blackListPagesToSocketConnect.some((pageHref) => {
    return window.location.pathname.includes(pageHref);
  });

  const storageService = useService(StorageService);
  const marketingCookies = storageService.get(
    localstorageKeys.marketingCookies,
    false,
  );

  const [functionalCookies, setFunctionalCookies] = useState(
    storageService.get(localstorageKeys.functionalCookies, false),
  );

  const handleFunctionalCookies = () => {
    const currentFunctionalCookies = storageService.get(
      localstorageKeys.functionalCookies,
      false,
    );
    setFunctionalCookies(currentFunctionalCookies);
  };

  useEffect(() => {
    storageService.onChange(
      localstorageKeys.bannerClosed,
      handleFunctionalCookies,
    );
  }, [storageService, handleFunctionalCookies]);

  if (marketingCookies) {
    initGoogleAnalytic();
  }

  const trackingLinksService = useService(TrackingLinksService);
  const dispatch = useDispatch();

  const { id } = useSelector(({ profileReducer }) => profileReducer);

  const { accessToken } = storageService.get(localstorageKeys.session, {});

  useClearOldStorageKeys(keyVersions);

  useEffect(() => {
    if (!accessToken) {
      sendGoogleAnalyticEvent({
        category: "User Interaction",
        action: "hon_visitors_without_registration",
        label: "Guest",
        value: 1,
        nonInteraction: true,
      });
    }
  }, [accessToken]);

  const onGrantAccess = useCallback(() => {
    const { accessToken } = storageService.get(localstorageKeys.session, {});
    const tourFinished = storageService.get(localstorageKeys.tourFinished, "");
    if (!tourFinished) {
      storageService.set(localstorageKeys.tourFinished, false);
    }

    if (accessToken && id) {
      dispatch(startSocket());
    }
  }, [dispatch, storageService, id]);

  useHandleConsentFormLink();

  useEffect(() => {
    storageService.onChange(localstorageKeys.session, onGrantAccess);
  }, [storageService, onGrantAccess]);

  useEffect(() => {
    if (accessToken && id && !isPageFromBlackList) {
      dispatch(startSocket());
    }

    return () => {
      dispatch(stopSocket());
    };
  }, [accessToken, dispatch, id, isPageFromBlackList]);

  const trackingCode = new URLSearchParams(document.location.search).get("tid");

  const handleSendClick = async () => {
    try {
      await trackingLinksService.sendClick(trackingCode);
    } catch (e) {
      console.log({ e });
    }
  };

  useEffect(() => {
    if (trackingCode && functionalCookies) {
      storageService.set(localstorageKeys.trackingCode, trackingCode);
      void handleSendClick();
      if (!accessToken) {
        const origin = window.location.origin;
        window.location.href = origin + "/";
        dispatch(toggleSignInModal(true));
      }
    }
  }, [trackingCode]);

  useEffect(() => {
    const lang = i18n.language;
    if (!lang || !LANGUAGE_OPTIONS[lang]) {
      // fallback to default language
      changeLanguage("en");
    }
  }, []);

  return <Context />;
};

export default App;
